/**
 * UpsellModal - Lightweight post-order upsell
 * 
 * Features:
 * - Shows 1-2 relevant items (drinks, desserts)
 * - Fully skippable with "No Thanks"
 * - Non-intrusive timing (after confirmation is clear)
 * - Simple, fast interaction
 */

import React from 'react';
import { X, Plus } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';

export default function UpsellModal({
  items = [], // Array of menu items to suggest
  onSelectItem, // (item) => void - when user selects an upsell item
  onSkip, // () => void - when user clicks "No Thanks"
  isVisible = false,
}) {
  if (!isVisible || items.length === 0) return null;

  // Limit to max 2 items as per spec
  const displayItems = items.slice(0, 2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onSkip}
      />

      {/* Modal */}
      <div 
        className="relative bg-[#262d3d] rounded-3xl max-w-2xl w-full mx-6 p-8 shadow-2xl border border-white/10 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">
            Would you like to add?
          </h2>
          <p className="text-lg text-gray-400">
            Complete your meal
          </p>
        </div>

        {/* Upsell Items */}
        <div className="grid grid-cols-1 gap-4 mb-6">
          {displayItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onSelectItem(item)}
              className="
                flex items-center gap-4
                bg-[#1a1f26] rounded-2xl p-4
                border-2 border-white/10
                hover:border-[#f5a623] hover:bg-[#1a1f26]/80
                transition-all duration-150
                active:scale-[0.98]
              "
            >
              {/* Item Image */}
              {item.image_url && (
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-700">
                  <img 
                    src={item.image_url} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Item Info */}
              <div className="flex-1 text-left">
                <h3 className="text-xl font-semibold text-white mb-1">
                  {item.name}
                </h3>
                {item.description && (
                  <p className="text-sm text-gray-400 line-clamp-1">
                    {item.description}
                  </p>
                )}
              </div>

              {/* Price & Add Button */}
              <div className="flex items-center gap-3">
                <span className="text-xl font-bold text-[#f5a623]">
                  {formatCurrency(item.price)}
                </span>
                <div className="w-12 h-12 rounded-xl bg-[#f5a623] flex items-center justify-center">
                  <Plus size={24} className="text-[#0f1419]" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* No Thanks Button */}
        <button
          onClick={onSkip}
          className="
            w-full h-14 rounded-xl
            bg-[#1a1f26] border border-white/20
            text-white text-lg font-medium
            hover:bg-white/5
            transition-colors
          "
        >
          No Thanks
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { 
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
